import React, { useEffect, useState } from "react";

import styles from "./_style/operbar";
import { observer } from "mobx-react";
import { HStack, XButton, XText } from "IZOX/_X";
import _ from "lodash";
import { IconX, ColorX, LocaleX } from "IZOX/STATIC";
import { v1 } from "uuid";

/**
 * @typedef {import("IZOX/__Typedef/common").lang} lang
 * @typedef {"Confirm" | "OK" | "Yes" | "No" | "Cancel" | "Clear"} btnType
 *
 * @typedef {Object} btnDef
 * @property {lang} label
 * @property {btnType} name
 * @property {JSX.Element | btnType} icon
 * @property {String | btnType} color
 * @property {String | btnType} hover
 * @property {Function} onPress
 * 
 * @typedef {btnType | btnDef} operbtns 
 * 
 * @param {Object} param0 
 * @param {Boolean} param0.disabled 
 * @param {Function} param0.onConfirm 
 * @param {Function} param0.onCancel 
 * @param {Function} param0.onClear 
 * @param {operbtns[]} param0.buttons  
 * @param {Number} param0.size
 * @param {String[]} param0.alwaysEnable - Array of button names that should always
 */
function OperBar({
  disabled,
  onConfirm,
  onCancel,
  onClear,
  size = 16,
  buttons = ["Cancel", "Confirm"],
  alwaysEnable = ["Cancel"]
}){

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    return () => {
      setLoading({}); 
    };
  }, []);

  const btnTextColor = (name, _d) => {
    if(_d) return ColorX.CSS("grey");
    switch(name){
      case "Yes": case "Confirm": 
        return ColorX.DayNight({day: "green", night: "dmBtnGreen"});
      case "Cancel": case "No": 
        return ColorX.DayNight({day: "red", night: "dmBtnRed"});
      case "OK": case "Clear": return ColorX.DayNight({day: "blue", night: "dmBtnBlue"});
      default: return ColorX.DayNight({day: "blue", night: "dmBtnBlue"});
    }
  };

  const btnIcon = (name) => {
    let color = btnTextColor(name);
    switch(name){
      default: case "OK": return IconX.Get("feather", "check", size, color);
      case "Cancel": return IconX.Get("mui", "block", size, color);
      case "Confirm": return IconX.Get("feather", "check", size, color);
      case "Yes": return IconX.Get("feather", "check", size, color);
      case "No": return IconX.Get("ant", "close", size, color);
      case "Clear": return IconX.Get("mui", "stream", size, color);
    }
  };

  const btnBgColor = (name, _d) => {
    if(_d) return ColorX.BgCSS("grey", 0.2);
    switch(name){
      case "Yes": case "Confirm": 
        return ColorX.BgCSS("green", 0.2);
      case "Cancel": case "No": 
        return ColorX.BgCSS("red", 0.2);
      case "OK": case "Clear": return ColorX.BgCSS("blue", 0.2);
      default: return ColorX.BgCSS("blue", 0.2);
    }
  };
  
  const btnHoverBgColor = (name, _d) => {
    if(_d) return ColorX.BgCSS("grey", 0.5);
    switch(name){
      case "Yes": case "Confirm": 
        return ColorX.BgCSS("green", 0.5);
      case "Cancel": case "No": 
        return ColorX.BgCSS("red", 0.5);
      case "OK": case "Clear": return ColorX.BgCSS("blue", 0.5);
      default: return ColorX.BgCSS("blue", 0.5);
    }
  };

  const btnText = (name) => {
    switch(name){
      default: case "OK": return {EN: "OK", TC: "確定"};
      case "Confirm": return {EN: "Confirm", TC: "確定"};
      case "Cancel": return {EN: "Cancel", TC: "取消"};
      case "Clear": return {EN: "Clear", TC: "清除"};
      case "Yes": return {EN: "Yes", TC: "是"};
      case "No": return {EN: "No", TC: "否"};
    }
  };

  const btnContent = (name) => {
    let color = btnTextColor(name);
    let text = btnText(name);
    return (
      <XText style={{...styles.btnText, color: color}} fontSize={size}>
        {LocaleX.Parse(text)}
      </XText>
    );
  };

  const btnFunc = async (name) => {
    switch(name){
      default: case "OK": case "Yes": case "Confirm": 
        return await onConfirm?.();
      case "Cancel": case "No": 
        return await onCancel?.();
      case "Clear": 
        return await onClear?.();
    }
  };

  const onPress = async (name) => {
    setLoading(true);
    await btnFunc(name);
    setLoading(false);
  };

  const onPressCustom = async (func) => {
    setLoading(true);
    await func();
    setLoading(false);
  };

  function renderButton(o){
    let color, func, hover, bgColor, name, icon, label;
    let _disabled = disabled && !alwaysEnable.includes(o);

    if(_.isString(o)){
      name = o;
      color = btnTextColor(o, _disabled);
      func = async () => await onPress(o);
      hover = btnHoverBgColor(o, _disabled);
      bgColor = btnBgColor(o, _disabled);
      icon = btnIcon(o);
      label = btnContent(o);
    }else{
      name = o.name ?? v1();
      color = _.isUndefined(o.color) ? btnTextColor(o.name, _disabled) : o.color;
      hover = _.isUndefined(o.hover) ? btnHoverBgColor(o.name, _disabled) : o.hover;
      bgColor = _.isUndefined(o.bgColor) ? btnBgColor(o.name, _disabled) : o.bgColor;
      icon = _.isUndefined(o.icon) ? btnIcon(o.name) : o.icon;
      label = _.isUndefined(o.label) ? btnContent(o.name) : 
        <XText bold>{LocaleX.Parse(o.label)}</XText>;
      func = _.isFunction(o.onPress) ? 
        async () => await onPressCustom(o.onPress) : 
        async () => await onPress(name);
    }
    
    return (
      <XButton padding={0} key={name}
        disabled={_disabled || loading}
        round={0} color={color} pV={5}
        onPress={func} style={styles.btn} 
        bgColor={bgColor}
        hoverBgColor={hover}>
        <HStack spacing={5}>
          {icon}
          {label}
        </HStack>
      </XButton>
    );
  }

  return (
    <HStack flexShrink={0} style={styles.btnBar}>
      {_.map(buttons, (o, i) => {
        return renderButton(o);
      })}
    </HStack>
  );
}

export default observer(OperBar);